import { EstimatorGraph as EsGra, EstimatorGraphBox, EstimatorMultipliedMetric, Metric } from '@dto/pageResponse.dto';
import { UnitUtil } from '@dto/util/UnitUtil';
import { ColorMap } from '@parts/graph/AbstractGraphPart';
import { MenuProps, Modal } from 'antd';
import { ItemType } from 'antd/es/menu/interface';
import type { Config, Datum, PlotRelayoutEvent, ScatterData } from 'plotly.js';
import Plotly from 'plotly.js/lib/core';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaExpand } from 'react-icons/fa';
import { useResizeDetector } from 'react-resize-detector';
import { ThemeUtil } from 'src/util/ThemeUtil';
import Plot from '../../PlotlyFactory';
import { XmSpinner } from '../spinner/XmSpinner';
import { XmTableScope } from '../table/XmTable';
import './graph.less';
import { GraphBtns } from './GraphBtns';
import { GraphPlotUtil } from './GraphPlotUtil';
import { colorSquare, ColorSquareParams } from './GraphTable';
import { createPlotlyLayout, GraphType, KTM, LayoutProps, PlotEl, PropsKTM } from './GraphTypes';
import { COLOR_SQUARE_WIDTH } from './XmColorSquare';

interface EstimatorGraphProps {
  box: EstimatorGraphBox;
  type: GraphType;
  tabName: string;
}

interface EstimatorKTMProps extends PropsKTM, EstimatorMultipliedMetric { }

export const XmEstimatorGraph: FC<EstimatorGraphProps> = (props) => {
  const { box, type } = props;
  const graph = box.graph;
  const [tscope] = useState(() => new XmTableScope<KTM<EstimatorKTMProps>>());
  const colorMap = useRef<Record<string, string>>({});
  const [modalFullscreenVisible, setModalFullscreenVisible] = useState(false);
  const [data, setData] = useState<Partial<ScatterData>[]>([]);
  const [tableDataSource, setTableDataSource] = useState<KTM[]>([]);
  const [tableDataTarget, setTableDataTarget] = useState<KTM[]>([]);
  const [tableDataLimit, setTableDataLimit] = useState<KTM[]>([]);
  const [plotVisible, setPlotVisible] = useState(true);
  const plotlyRef = useRef<PlotEl>(undefined);
  const maxVal = useRef(0);

  const [propsFS, setPropsFS] = useState(props);

  const [dropdownItems] = useState<MenuProps>(() => {
    const items: ItemType[] = [];
    if (type === GraphType.regular) {
      items.push({
        key: 'fullscreen',
        icon: <FaExpand />,
        label: 'Fullscreen',
        title: 'Show graph in fullscreen',
        onClick: openFullscreen
      });
    }
    return { items };
  });

  const [config] = useState<Partial<Config>>(() => ({
    displaylogo: false, autosizable: true, responsive: true, displayModeBar: true,
    modeBarButtons: [[]],
  }));

  const [metricLimit] = useState<Metric>(() => ({ ...graph.sourceLoad.metrics[0].metric, metric: 'cpuLimit', label: 'Limit', color: ThemeUtil.getColor('black'), type: 'line', stacked: false }));

  const layoutProps = useMemo<LayoutProps>(() => ({ ...props, box: { ...props.box, metrics: getMetrics() } }), [props]);

  const layout = useMemo(() => {
    const pl = createPlotlyLayout(layoutProps);
    pl.xaxis.fixedrange = true;
    pl.yaxis.fixedrange = true;
    pl.yaxis.title = { text: graph.targetMetricName };
    return pl;
  }, [props]);

  const onResize = useCallback(() => {
    if (!plotlyRef.current?._fullData?.length)
      return;
    Plotly.Plots.resize(plotlyRef.current);
  }, []);
  const { ref } = useResizeDetector<HTMLDivElement>({ onResize, skipOnMount: true, refreshMode: 'debounce' });

  useEffect(() => {
    updateData();
  }, [props]);

  function getMetrics() {
    return [...[graph.sourceLoad, graph.targetLoad].filter(Boolean).flatMap(load => load.metrics.map(emm => emm.metric)), metricLimit];
  }

  function openFullscreen() {
    setPropsFS({ ...props, type: GraphType.modal });
    setModalFullscreenVisible(true);
  }

  function createPlotData(emm: EstimatorMultipliedMetric, tableData: Record<string, KTM<EstimatorKTMProps>>) {
    const { label: name, metric: metricObj, item_id: uid } = emm;

    const pd: Partial<ScatterData> = {
      x: Object.keys(emm.timeseries),
      y: Object.values(emm.timeseries),
      type: 'scatter',
      name,
      line: { color: metricObj?.color ?? colorMap.current[uid], width: 1 },
      meta: { uid, name, itemName: name, metric: metricObj?.label },
      visible: true,
    };
    GraphPlotUtil.assignHoverTemplate(pd, layout.hovermode);

    if (metricObj.stacked) {
      pd.stackgroup = metricObj.metric;
    }

    if (metricObj.type === 'area' && !metricObj.stacked) {
      const gapsY: Datum[] = [];
      const gapsX: Datum[] = [];
      pd.y.forEach((value, index) => {
        if (value === null) {
          // start gap
          if (index > 0 && pd.y[index - 1] !== null) {
            gapsY.push(0);
            gapsX.push(pd.x[index - 1] as number + 1);
          }
          // end gap
          if (index < pd.y.length - 1 && pd.y[index + 1] !== null) {
            gapsY.push(value);
            gapsX.push(pd.x[index] as Datum);

            // area gap must be surrounded by 0 otherwise it continues to next value
            // single value spikes in stacked graph should not have this
            gapsY.push(0);
            gapsX.push(pd.x[index + 1] as number - 1);
            return;
          }
        }
        gapsY.push(value);
        gapsX.push(pd.x[index] as Datum);
      });
      pd.y = gapsY;
      pd.x = gapsX;
    }

    const isBi = UnitUtil.isBiUnit(metricObj?.unit);
    pd.customdata = (pd.y as Datum[]).map(y => y == null ? null : UnitUtil.formatSI(Math.abs(y as number), 2, isBi, metricObj));

    if (!tableData[uid]) {
      const ktm = new KTM<EstimatorKTMProps>();
      ktm.key = uid;
      ktm.name = name;
      ktm.metric = metricObj.metric;
      ktm.uid = uid;
      ktm.props = emm;
      tableData[uid] = ktm;
    }

    return pd;
  }

  function updateData() {
    const colors = new ColorMap();
    GraphPlotUtil.assignUniqueColors([...graph.sourceLoad.metrics, ...(graph.targetLoad?.metrics || [])].map(emm => emm.item_id), colors);
    colorMap.current = colors.colorPalette;

    const plotData: Partial<ScatterData>[] = [];
    const tableDataSource: Record<string, KTM<EstimatorKTMProps>> = {};
    const tableDataTarget: Record<string, KTM<EstimatorKTMProps>> = {};
    const tableDataLimit: Record<string, KTM<EstimatorKTMProps>> = {};

    for (const emm of graph.sourceLoad.metrics) {
      plotData.push(createPlotData(emm, tableDataSource));
    }
    for (const emm of graph.targetLoad?.metrics ?? []) {
      plotData.push(createPlotData(emm, tableDataTarget));
    }
    for (const ep of graph.extraProps ?? []) {
      const pd = createPlotData({
        fix: null, max: null, metric: metricLimit,
        avg: ep.value, smt: ep.smt,
        item_id: ep.label, label: ep.label,
        //TODO: timezone not matching with timeseries vs. dataRange
        timeseries: {
          [new Date(graph.dateRange.start * 1000).toISOString()]: ep.value,
          [new Date((graph.dateRange.start + graph.dateRange.end) / 2 * 1000).toISOString()]: ep.value,
          [new Date(graph.dateRange.end * 1000).toISOString()]: ep.value
        }
      }, tableDataLimit);
      pd.mode = 'lines';
      plotData.push(pd);
    }

    const sourceTbl = Object.values(tableDataSource);
    const targetTbl = Object.values(tableDataTarget);
    const limitTbl = Object.values(tableDataLimit);

    maxVal.current = GraphPlotUtil.maxY(plotData);
    GraphPlotUtil.assignTickFormat([...sourceTbl, ...targetTbl, ...limitTbl], plotData, layoutProps, layout, maxVal.current);
    setData(plotData);
    setTableDataSource(sourceTbl);
    setTableDataTarget(targetTbl);
    setTableDataLimit(limitTbl);
  }

  function onRelayout(event: Readonly<PlotRelayoutEvent>) {
    if (event.autosize || !event.hovermode) return;
    layout.hovermode = event.hovermode;
    refreshHoverMode();
  }

  function refreshHoverMode() {
    data.forEach(pd => GraphPlotUtil.assignHoverTemplate(pd, layout.hovermode));
    setData(prev => [...prev]);
  }

  const svgContainerEl = ref.current?.querySelector('.svg-container');

  const colorParams = (tableData: KTM[], setTableData: (tableData: KTM[]) => void): ColorSquareParams => ({
    data, tableData, setData, setTableData, setPlotVisible,
    assignTickFormat: (tblData, plotData?) => GraphPlotUtil.assignTickFormat(tblData, plotData || data, layoutProps, layout, maxVal.current)
  });

  return (
    <>
      <div ref={ref} className="xm-graph">
        {plotVisible ? <>
          {
            type !== GraphType.dashboard && svgContainerEl &&
            // for tooltip z-index
            createPortal(<GraphBtns dropdownItems={dropdownItems} layout={layout} refresh={refreshHoverMode}
              modebarBtns={config.modeBarButtons as [][]} />, svgContainerEl)
          }
          <Plot ref={r => { plotlyRef.current = r; }}
            className="xm-plotly js-plotly-plot"
            data={data} layout={{ ...layout }} onRelayout={onRelayout}
            useResizeHandler config={config} />
        </>
          : <div style={{ height: layout.height }}><XmSpinner /></div>}

        <LoadGrid key="graph-grid-source"
          tableData={tableDataSource}
          title={graph.sourceLoad.headerLabel}
          graph={graph}
          colorParams={colorParams(tableDataSource, setTableDataSource)} />

        {graph.targetLoad && <LoadGrid key="graph-grid-target"
          tableData={tableDataTarget}
          title={graph.targetLoad.headerLabel}
          graph={graph}
          colorParams={colorParams(tableDataTarget, setTableDataTarget)} />}

        <tscope.XmTable
          key="graph-grid-limit"
          dataSource={tableDataLimit}
          scroll={{ y: '140px' }}
          pagination={false}
          bordered
          title={() => <strong>Target Server CPU limit</strong>}
        >
          {[
            <tscope.Col key="metricColor" className="square-td" width={COLOR_SQUARE_WIDTH}
              render={(value, record) => colorSquare(record, undefined, colorParams(tableDataLimit, setTableDataLimit))} />,

            <tscope.Col
              key="name"
              className="xm-td-ellipsis"
              title="Name"
              dataIndex="name"
              render={value => <div className="xm-ellipsis"><div>{value}</div></div>}
            />,

            <tscope.Col
              key="met"
              title={graph.targetMetricName}
              width="12em"
              dataIndex={['props', 'avg']}
            />,

            <tscope.Col
              key="smt"
              title="SMT"
              width="12em"
              dataIndex={['props', 'smt']}
            />,
          ]}
        </tscope.XmTable>
      </div>

      {type !== GraphType.modal &&
        <Modal centered open={modalFullscreenVisible} onCancel={() => setModalFullscreenVisible(false)} title="CPU Workload Estimator"
          destroyOnHidden closable={false} footer={null} width="90%">
          <XmEstimatorGraph {...propsFS} />
        </Modal>}
    </>
  );
};

interface LoadGridProps {
  tableData: KTM<EstimatorKTMProps>[];
  title: string;
  colorParams: ColorSquareParams;
  graph: EsGra;
}

const LoadGrid: FC<LoadGridProps> = ({ tableData, title, colorParams, graph: { targetMetricName, ...graph } }) => {
  const [tscope] = useState(() => new XmTableScope<KTM<EstimatorKTMProps>>());
  const precision = graph.sourceLoad.metrics[0].metric.defaultPrecision || 2;

  return <tscope.XmTable
    key="graph-grid"
    dataSource={tableData}
    scroll={{ y: '140px' }}
    pagination={false}
    bordered
    title={() => <strong>{title}</strong>}
  >
    {[
      <tscope.Col key="metricColor" className="square-td" width={COLOR_SQUARE_WIDTH}
        render={(value, record) => colorSquare(record, undefined, colorParams)} />,

      <tscope.Col
        key="name"
        className="xm-td-ellipsis"
        title="Name"
        dataIndex="name"
        render={value => <div className="xm-ellipsis"><div>{value}</div></div>}
      />,

      <tscope.Col
        key="parent"
        className="xm-td-ellipsis"
        title="Server"
        dataIndex={['props', 'parentLabel']}
        width="25em"
        render={value => <div className="xm-ellipsis"><div>{value}</div></div>}
      />,

      <tscope.Col
        key="avg"
        title={`Avg [${targetMetricName}]`}
        width="6em"
        dataIndex={['props', 'avg']}
        render={(value: number) => value?.toFixed(precision)}
      />,

      <tscope.Col
        key="max"
        title={`Max [${targetMetricName}]`}
        width="6em"
        dataIndex={['props', 'max']}
        render={(value: number) => value?.toFixed(precision)}
      />,

      <tscope.Col
        key="smt"
        title="SMT"
        width="6em"
        dataIndex={['props', 'smt']}
      />,
    ]}
  </tscope.XmTable>;
};
